import type {
	AbschnittCCandidateGender,
	AbschnittCElection, AbschnittCElection19EWResult,
	AbschnittCElectionCandidate,
	CandidateId
} from "@/lib/abschnittc/AbschnittCElection";
import type {
	AbschnittCElectionResult,
	AbschnittCElectionResultCommons,
	AbschnittCElectionResultComplete,
	AbschnittCElectionResultMessage,
	AbschnittCElectionResultMessageApproval,
	AbschnittCElectionResultMessageAverages,
	AbschnittCElectionResultMessageDirectComparison,
	AbschnittCElectionResultMessageMinSpotMatch
} from "@/lib/abschnittc/AbschnittCElectionResult";
import type { PdfDocumentManager } from "@/lib/pdflib2/PdfDocumentManager";
import type { PdfDocument } from "@/lib/pdflib2/PdfDocument";
import type { PdfFont } from "@/lib/pdflib2/PdfFont";
import type { PdfImage } from "@/lib/pdflib2/PdfImage";
import { type PdfBufferedVirtualWriter, pdfVirtualWriter } from "@/lib/pdflib2/PdfBufferedVirtualWriter";
import { PdfGeometries } from "@/lib/pdflib2/PdfGeometries";
import debug, { log } from "debug";
import { multilineText, rightAlignedText } from "@/lib/pdflib2/PdfText";
import { color, type PdfColor } from "@/lib/pdflib2/PdfColor";
import type { TextProvider, TextProviderGroup } from "@/lib/TextProvider";
import { objectGroup } from "@/lib/pdflib2/PdfObjectGroup";
import { vector2d } from "@/lib/pdflib2/Vector2d";
import { whitespace } from "@/lib/pdflib2/PdfWhitespace";
import { abschnittCCandidateName } from "@/lib/abschnittc/utility/AbschnittCCandidateName";
import { abschnittCElectionMaxPoints } from "@/lib/abschnittc/utility/AbschnittCElectionMaxPoints";
import { equalsWithEpsilon } from "@/lib/utility";
import { line } from "@/lib/pdflib2/PdfLine";
import type { PdfPage } from "@/lib/pdflib2/PdfPage";

const debugLog = debug("abschnittc:AbschnittCElectionResultSheetRenderer");

export interface AbschnittCElectionResultSheetRendererOptions {
	election: AbschnittCElection;
	result: AbschnittCElectionResultComplete & AbschnittCElectionResultCommons;
	pdfDocumentManager: PdfDocumentManager;
	textProvider: TextProvider;
	gitRevision: string;
	gitAuthor: string;
}

export class AbschnittCElectionResultSheetRenderer {

	private document?: PdfDocument;
	private writer: PdfBufferedVirtualWriter;
	private assets?: {
		regular: PdfFont;
		italic: PdfFont;
		bold: PdfFont;
		logo: PdfImage;
	};
	private colors: {
		black: PdfColor;
		lightGray: PdfColor;
	};
	private group: TextProviderGroup;
	private fractionDigits = 4;

	private pagePaddings = {
		top: 19, right: 13.2, bottom: 36.7, left: 19
	};

	constructor(private options: AbschnittCElectionResultSheetRendererOptions) {
		this.writer = pdfVirtualWriter(PdfGeometries.A4
			.padding(this.pagePaddings.top, this.pagePaddings.right, this.pagePaddings.bottom, this.pagePaddings.left));

		this.colors = {
			black: color(0, 0, 0),
			lightGray: color(0.8, 0.8, 0.8)
		};

		this.group = this.options.textProvider.group("abschnittCResult");
	}

	async addAssets(): Promise<void> {

		const UbuntuR = (await import("@/assets/fonts/Ubuntu-R.ttf?arraybuffer")).default;
		const UbuntuRI = (await import("@/assets/fonts/Ubuntu-RI.ttf?arraybuffer")).default;
		const UbuntuB = (await import("@/assets/fonts/Ubuntu-B.ttf?arraybuffer")).default;
		const VoltLogo = (await import("@/assets/img/logo-dark.png?arraybuffer")).default;

		this.assets = {
			regular: await this.document!.addFont(UbuntuR),
			italic: await this.document!.addFont(UbuntuRI),
			bold: await this.document!.addFont(UbuntuB),
			logo: await this.document!.addImage(VoltLogo, "png")
		};
	}

	joinWithUnd(names: string[]) {
		if (names.length === 0)
			return "";
		if (names.length === 1)
			return names[0];
		return names.slice(0, -1).join(", ") + " und " + names[names.length - 1];
	}

	async setup(): Promise<void> {
		this.document = await this.options.pdfDocumentManager.create();
		await this.addAssets();
	}

	skip(height: number) {
		this.writer.addObject(whitespace(vector2d(0, height)));
	}

	text(...paragraphs: string[]) {

		for (const p of paragraphs) {
			this.writer.addObject(multilineText(p, this.assets!.regular.atSize(11),
				this.colors.black, this.writer.safeGeometryWidth()));
			this.skip(this.margins().afterParagraph);
		}
	}

	indentedText(...paragraphs: string[]) {

		for (const p of paragraphs) {
			this.writer.addObject(objectGroup()
				.addRelative(multilineText(p, this.assets!.regular.atSize(11),
					this.colors.black, this.writer.safeGeometryWidth()), vector2d(10, 0)));
			this.skip(this.margins().afterParagraph);
		}
	}

	title(text: string) {
		this.skip(8);
		this.writer.addObject(multilineText(text, this.assets!.bold.atSize(14),
			this.colors.black, this.writer.safeGeometryWidth()));
		this.skip(this.margins().afterParagraph);
	}

	h2(text: string) {
		this.skip(6);
		this.writer.addObject(multilineText(text, this.fixedFonts().h2,
			this.colors.black, this.writer.safeGeometryWidth()));
		this.skip(this.margins().afterParagraph);
	}

	h3(text: string) {
		this.skip(4);
		this.writer.addObject(multilineText(text, this.fixedFonts().h3,
			this.colors.black, this.writer.safeGeometryWidth()));
		this.skip(this.margins().afterParagraph);

	}

	margins() {
		return {
			beforeParagraph: 4,
			afterParagraph: 2
		};
	}

	list(texts: string[]) {

		const { beforeParagraph, afterParagraph } = this.margins();

		const { regular } = this.fixedFonts();

		for (const text of texts) {
			this.writer.addObject(
				objectGroup()
					.addRelative(
						multilineText("•", regular, this.colors.black, 10),
						vector2d(5, 0)
					)
					.addRelative(
						multilineText(text, regular, this.colors.black, this.writer.safeGeometryWidth()),
						vector2d(10, 0)
					)
			);
			this.skip(1);
		}
		this.skip(afterParagraph);
	}

	fixedFonts() {
		return {
			regular: this.assets!.regular.atSize(11),
			bold: this.assets!.bold.atSize(11),
			h1: this.assets!.bold.atSize(16),
			h2: this.assets!.bold.atSize(14),
			h3: this.assets!.bold.atSize(12)
		};
	}

	table(options: {
		headers: string[],
		rows: string[][],
		widths?: number[]
	}) {

		const { headers, rows } = options;
		const { black, lightGray } = this.colors;

		const rw = options.widths ?? headers.map(() => 1 / headers.length);
		const sum = rw.reduce((a, b) => a + b, 0);
		const maxWidth = this.writer.safeGeometryWidth();
		const widths = rw.map(value => (value / sum) * maxWidth);

		let x = 0;
		const { regular, bold } = this.fixedFonts();

		const headerLine = objectGroup();
		for (let i = 0; i < headers.length; i++) {
			headerLine.addRelative(multilineText(headers[i], bold, black, widths[i]), vector2d(x, 0));
			x += widths[i];
		}

		const skipPt = 0.8;
		const skipPb = 3 - skipPt;

		this.skip(2);
		this.writer.addObject(headerLine);
		this.skip(skipPb);
		this.writer.addObject(line(vector2d(0, 0), vector2d(maxWidth, 0), lightGray, 1.5));
		this.skip(skipPt);

		for (const row of rows) {
			const current = objectGroup();
			x = 0;

			for (let i = 0; i < rows.length; i++) {
				const cell = row[i];

				if (cell && cell.length > 0) {
					current.addRelative(multilineText(cell, regular,
						black, widths[i]), vector2d(x, 0));
				}
				x += widths[i];
			}

			this.writer.addObject(current);
			this.skip(skipPb);
			this.writer.addObject(line(vector2d(0, 0), vector2d(maxWidth, 0), lightGray, 0.5));
			this.skip(skipPt);
		}

		this.skip(this.margins().afterParagraph);
	}

	documentTitle() {

		const logoWidth = 30;
		const logo = this.assets!.logo.atWidth(logoWidth);
		const maxSafeWidth = this.writer.safeGeometryWidth();

		const { black } = this.colors;
		const { settings } = this.options.election;

		this.writer.addObject(
			objectGroup()
				.addRelative(logo, vector2d(maxSafeWidth - logoWidth, 0))
				.addFlow(multilineText(this.group.get("title")
					.set("eventName", settings.eventName)
					.get(), this.assets!.bold.atSize(16), black, maxSafeWidth - logoWidth - 10))
				.addFlow(whitespace(vector2d(0, 2.5)))
				.addFlow(multilineText(this.group.get("subtitle")
					.set("what", settings.what)
					.get(), this.assets!.regular.atSize(16), black, maxSafeWidth - logoWidth - 10))
		);

	}

	async generate(): Promise<ArrayBuffer> {

		await this.setup();

		this.documentTitle();

		this.skip(this.margins().afterParagraph);

		this.general();

		this.candidates();

		this.lead();

		this.first();

		this.second();

		this.final();

		this.display();

		this.signatures();

		this.writer.complete(this.document!);

		const pages = this.document!.pages();

		console.log(pages);
		for (let i = 0; i < pages.length; i++) {
			this.eachPage(pages[i], i, pages.length);
		}

		return await this.options.pdfDocumentManager.exportAsArrayBuffer(this.document!);
	}

	eachPage(page: PdfPage, i: number, total: number) {

		const size = page.size();
		const location = vector2d(this.pagePaddings.left, size.height() - this.pagePaddings.bottom + 20);

		const text = `Erstellt mit "Vint - Demokratie & Transparenz bei Volt" (volt.link/vint) - Seite ${i + 1} von ${total}`;
		console.log(text, location);
		page.addFixedObject(
			location,
			rightAlignedText(text, this.fixedFonts().regular
				.atSize(9), this.colors.black, this.writer.safeGeometryWidth())
		);

	}

	general() {
		// Allgemeines
		const rg = this.group.sub("general");
		const { settings } = this.options.election;

		this.title(rg.get("title").get());

		this.text(rg.get("explanation").get());

		const now = new Date();

		this.text(rg.get("date")
			.set("date", now.toLocaleDateString("de-DE"))
			.set("time", now.toLocaleTimeString("de-DE"))
			.set("gitRevision", this.options.gitRevision)
			.set("gitAuthor", this.options.gitAuthor)
			.get());

		this.text(rg.get("zkLead")
			.set("zkLead", settings.zkLead)
			.get());

		const zkMembers = settings.zkMembers;

		if (zkMembers.length > 0)
			this.text(rg.get("zkMember")
				.set("zkMembers", this.joinWithUnd(zkMembers))
				.get());

		if (settings.listLimitParity) {
			if (settings.listLimit > 0) {
				this.text(rg.get("listLimitBoth")
					.set("listLimit", settings.listLimit.toString())
					.get());
			} else {
				this.text(rg.get("listLimitParity")
					.get());
			}
		} else if (settings.listLimit > 0) {
			this.text(rg.get("listLimit")
				.set("listLimit", settings.listLimit.toString())
				.get());
		} else {
			this.text(rg.get("listLimitNone").get());
		}

		if (settings.leadElectionSeats > 0) {
			this.text(rg.get("leadElections")
				.set("leadElectionSeats", settings.leadElectionSeats.toString()).get());
		}

	}

	second() {
		const q = this.group.sub("second");
		this.h2(q.get("title").get());

		if (this.options.election.settings.leadElectionSeats > 0) {
			this.text(q.get("topSkip")
				.set("spots", this.options.election.settings.leadElectionSeats)
				.get());
		} else {

			this.text(q.get("who").get());

			const runoff = this.options.result.runoffCandidates;
			const names = runoff.map(value => this.candidateName(value));

			this.list(names);

			this.runoff();
		}
	}

	final() {
		const q = this.group.sub("final");
		this.h2(q.get("title").get());

		let start = this.options.election.settings.leadElectionSeats > 0 ? this.options.election.settings.leadElectionSeats + 1 : 1;

		const previous = start - 1;
		const lastCandidateId = this.options.result.spots[previous];
		const lastCandidate = this.options.election.candidates.find(value => value.id === lastCandidateId)!;

		this.text(q.get("parity")
			.set("spot", start)
			.set("listLongOther", this.listLong(lastCandidate.list))
			.set("listLong", this.listLong(lastCandidate.list === "male" ? "female" : "male"))
			.get());

		for (let i = start; i < this.options.result.spots.length; i++) {
			const displaySpot = i + 1;

			const candidateId = this.options.result.spots[i];
			const candidate = this.options.election.candidates.find(value => value.id === candidateId)!;

			// unmatched
			const message = this.options.result.messages.find(value => {
				return value.type === "minSpotMatch" && value.spot === displaySpot;
			})! as AbschnittCElectionResultMessageMinSpotMatch;

			if (!message)
				throw new Error(`No min spot match message found for spot ${displaySpot}`);

			this.h3(q.get("spot").set("spot", displaySpot).get());

			if (message.skippedCandidates.length > 0) {
				for (const skipped of message.skippedCandidates) {
					const candidate = this.options.election.candidates.find(value => value.id === skipped)!;
					this.text(q.get("candidateSkip")
						.set("listLong", this.listLong(candidate.list))
						.set("candidateName", this.candidateName(skipped))
						.set("minSpot", candidate.minSpot)
						.set("spot", displaySpot).get());
				}
			}

			this.text(q.get("match")
				.set("listLong", this.listLong(candidate.list))
				.set("candidateName", abschnittCCandidateName(candidate))
				.set("spot", displaySpot).get());

			this.text(q.get("acceptance")
				.set("candidateName", abschnittCCandidateName(candidate))
				.set("spot", displaySpot).get());
		}

		if (this.options.result.endReason === "list-limit") {
			this.text(q.get("lengthEnded")
				.set("listLimit", this.options.election.settings.listLimit)
				.get());
		} else if (this.options.result.endReason === "parity-limit") {
			this.text(q.get("parityEnded").get());
		}

	}

	display() {
		const q = this.group.sub("display");
		this.h2(q.get("title").get());

		this.text(q.get("explanation").get());

		this.table({
			headers: ["Platz", "Name"],
			rows: this.options.result.spots.map((value, index) => {
				const candidate = this.options.election.candidates.find(v => v.id === value)!;
				return [`${index + 1}`, abschnittCCandidateName(candidate)];
			}),
			widths: [1, 7]
		});

		this.skip(10);

	}

	signatures() {
		const q = this.group.sub("signatures");

		const group = objectGroup();

		group.addFlow(
			multilineText(q.get("greeting")
					.set("eventName", this.options.election.settings.eventName).get(),
				this.fixedFonts().regular,
				this.colors.black, this.writer.safeGeometryWidth()))
			.addFlow(whitespace(vector2d(0, this.margins().afterParagraph)))
			.addFlow(
				multilineText(q.get("location")
						.set("location", this.options.election.settings.location)
						.set("date", (new Date()).toLocaleDateString("de-DE")).get(),
					this.fixedFonts().regular,
					this.colors.black, this.writer.safeGeometryWidth()))
			.addFlow(whitespace(vector2d(0, 25)));

		const names = [this.options.election.settings.zkLead, ...this.options.election.settings.zkMembers];
		const count = Math.min(names.length, 3);

		const gap = 10;
		const lineWidth = (this.writer.safeGeometryWidth() - gap * 2) / count;
		const linesStarts = [0, lineWidth + gap, 2 * (lineWidth + gap)];

		const innerGroup = objectGroup();

		for (let i = 0; i < count; i++) {
			innerGroup
				.addRelative(
					line(
						vector2d(0, 0), vector2d(lineWidth, 0),
						this.colors.black, 1), vector2d(linesStarts[i], 0))
				.addRelative(
					multilineText(
						names[i], this.fixedFonts().regular, this.colors.black, lineWidth),
					vector2d(linesStarts[i], 1)
				);
		}

		group.addFlow(innerGroup);

		this.writer.addObject(group);

	}

	static sortCandidateByName(a: AbschnittCElectionCandidate, b: AbschnittCElectionCandidate) {
		if (a.lastName !== b.lastName)
			return a.lastName.localeCompare(b.lastName);
		else if (a.firstName !== b.firstName)
			return a.firstName.localeCompare(b.firstName);
		else if (a.title !== b.title)
			return a.title?.localeCompare(b.title ?? "") ?? 0;
		else if (a.extra !== b.extra)
			return a.extra?.localeCompare(b.extra ?? "") ?? 0;
		else
			return 0;
	}

	candidates() {
		const cg = this.group.sub("candidates");
		// Candidates

		this.title(cg.get("title").get());

		for (const gender of this.genders()) {

			this.text(cg.get("nominated", gender).get());

			const candidates = this.options.election.candidates
				.filter(value => value.list === gender);
			const sorted = candidates
				.sort((a, b) => AbschnittCElectionResultSheetRenderer.sortCandidateByName(a, b));

			this.table({
				headers: ["Titel", "Vorname", "Nachname", "", "Ab Listenplatz"],
				rows: sorted.map(value => {
					return [
						value.title ?? "",
						value.firstName,
						value.lastName,
						value.extra ?? "",
						value.minSpot.toString()];
				}),
				widths: [1, 3, 3, 1, 2]
			});

			this.skip(this.margins().afterParagraph);
		}

	}

	ew19(ew: AbschnittCElection19EWResult, spot: number, first: boolean) {
		const q = this.group.sub("ew19");

		if (first) {
			this.text(q.get("countAndSecret")
				.set("valid", ew.total)
				.set("secret", ew.secret)
				.get());
		} else {
			const names = ew.candidateIds.map(v => this.candidateName(v));
			this.list(names);

			this.text(q.get("notElectedQuorum2")
				.set("valid", ew.total)
				.set("secret", ew.secret)
				.get());
		}

		const total = Object.values(ew.votes).reduce((a, b) => a + b);
		const quorum = Math.floor(total * 0.5) + 1;

		console.log({ ...ew.votes }, ew.candidateIds);

		const rows: string[][] = [];
		let winner = undefined;

		for (const value of ew.candidateIds) {
			const candidate = this.options.election.candidates.find(v => v.id === value)!;

			const votes = ew.votes[value];
			if (votes > quorum)
				winner = candidate;

			rows.push([
				abschnittCCandidateName(candidate),
				votes.toString(),
				`${(votes / total * 100).toFixed(2)} %`
			]);
		}

		rows.sort((a, b) => parseInt(b[1]) - parseInt(a[1]));

		rows.push(["Enthaltungen", ew.abstentions.toString(), ""]);
		rows.push(["Gültige Stimmen", ew.total.toString(), ""]);
		rows.push(["Ungültige Stimmen", ew.invalid.toString(), ""]);

		this.table({
			headers: ["", "Stimmen", "Anteil"],
			rows,
			widths: [5, 1, 1]
		});

		if (winner) {
			const winnerName = abschnittCCandidateName(winner);

			this.text(q.get("electedQuorum")
				.set("candidateName", winnerName)
				.set("spot", spot)
				.set("quorum", quorum).get());

			this.text(q.get("acceptance")
				.set("candidateName", winnerName)
				.set("spot", spot).get());

		} else {
			this.text(q.get("notElectedQuorum1")
				.set("quorum", quorum).get());
		}
	}

	runoff() {

		let first = true;

		for (const res of this.options.election.runoffResults) {
			this.ew19(res, 1, first);
			first = false;
		}
	}

	lead() {
		if (this.options.election.settings.leadElectionSeats === 0)
			return;

		const q = this.group.sub("lead");

		this.h2(q.get("title").get());

		// TODO
	}

	candidateName(id: CandidateId) {
		const candidate = this.options.election.candidates.find(value => value.id === id)!;
		return abschnittCCandidateName(candidate);
	}

	first() {

		const fg = this.group.sub("first");

		// First
		this.title(fg.get("title").get());

		this.firstVoting();

		this.firstApproval();

		this.preliminary();

		this.spots();

		this.overview();

	}

	getMessages<T extends AbschnittCElectionResultMessage>(type: T["type"]): T[] {
		return this.options.result.messages.filter(value => value.type === type) as T[];
	}

	overview() {
		const q = this.group.sub("first").sub("overview");

		this.h2(q.get("title").get());

		this.text(q.get("explanation").get());

		for (const gender of this.genders()) {
			const list = this.options.result.preliminaryLists[gender];

			this.h3(q.get("list", gender).get());

			this.table({
				headers: ["Nr.", "Titel", "Vorname", "Nachname", "", "Mittelwert", "Vergleich"],
				rows: list.map(((v, ix) => {
					const candidate = this.options.election.candidates.find(value => value.id === v.candidate)!;
					return [
						`${ix + 1}`,
						candidate.title ?? "",
						candidate.firstName,
						candidate.lastName,
						candidate.extra ?? "",
						v.score.toFixed(this.fractionDigits),
						`${v.shift}`
					];
				})),
				widths: [1, 1, 3, 3, 1, 1.5, 1.5]
			});
		}
	}


	preliminary() {
		const pg = this.group.sub("first").sub("preliminary");

		this.h2(pg.get("title").get());

		const averages = this.getMessages<AbschnittCElectionResultMessageAverages>("averages")
			.map(value => {
				return {
					message: value,
					candidate: this.options.election.candidates.find(v => v.id === value.candidateId)!
				};
			});

		const sorted = averages.sort((a, b) => {
			if (a.candidate.list !== b.candidate.list) {
				if (a.candidate.list === "male") return 1;
				else return -1;
			} else {
				if (equalsWithEpsilon(a.message.score, b.message.score, 1e-5)) {
					return AbschnittCElectionResultSheetRenderer.sortCandidateByName(a.candidate, b.candidate);
				} else {
					return b.message.score - a.message.score;
				}
			}
		});

		for (const gender of this.genders()) {
			this.text(pg.get("averages", gender).get());
			this.table({
				headers: ["Titel", "Vorname", "Nachname", "", "Mittelwert", "n"],
				rows: sorted.filter(v => v.candidate.list === gender)
					.map(value => {
						return [
							value.candidate.title ?? "",
							value.candidate.firstName,
							value.candidate.lastName,
							value.candidate.extra ?? "",
							value.message.score.toFixed(this.fractionDigits),
							value.message.total.toString()
						];
					}),
				widths: [1, 3, 3, 1, 1.5, 1]
			});
		}

		this.text(pg.get("explanation").get());
	}

	genders(): AbschnittCCandidateGender[] {
		return ["male", "female"];
	}

	listLong(gender: AbschnittCCandidateGender) {
		return this.group.get("listLong", gender).get();
	}

	spots() {
		const totalSpots = this.options.result.spots.length;
		const start = this.options.election.leadElections.length;

		for (const gender of this.genders()) {
			for (let i = 0; i < this.options.result.preliminaryLists[gender].length; i++)
				this.spot(i, gender);
		}
	}

	spot(n: number, gender: AbschnittCCandidateGender) {
		const q = this.group.sub("first").sub("spot");

		const displaySpot = n + 1;
		const isLast = n - 1 >= this.options.result.preliminaryLists[gender].length;
		const listText = this.listLong(gender);

		this.h3(q.get("title")
			.set("spot", displaySpot)
			.set("listLong", listText)
			.get()
		);

		const dcResultMessage = this.options.result.messages
			.find(value => {
				return value.type === "directComparison"
					&& value.result.spot === displaySpot && value.result.list === gender;
			}) as AbschnittCElectionResultMessageDirectComparison;
		if (!dcResultMessage)
			throw new Error("No direct comparison result found");
		const dcResult = dcResultMessage.result;

		if (dcResult.type === "last") {
			this.text(q.get("last")
				.set("spot", displaySpot)
				.set("listLong", listText)
				.get());
		} else {
			// selection
			const selection = dcResult.selectResult;

			if (selection.type === "pair") {

				const names = [this.candidateName(selection.first), this.candidateName(selection.second)];
				this.text(q.get("compCandidates")
					.set("candidateNames", this.joinWithUnd(names))
					.set("score1", selection.firstScore.toFixed(this.fractionDigits))
					.set("score2", selection.secondScore.toFixed(this.fractionDigits))
					.get());

			} else if (selection.type === "lead-and-tie") {

			} else if (selection.type === "tie") {

			}

			// result
			this.text(q.get("dc1").get());
			this.indentedText(q.get("dc2")
				.set("candidateName1", this.candidateName(dcResult.winner))
				.set("candidateName2", this.candidateName(dcResult.loser))
				.set("count", dcResult.winnerCount).get());

			this.indentedText(q.get("dc2")
				.set("candidateName1", this.candidateName(dcResult.loser))
				.set("candidateName2", this.candidateName(dcResult.winner))
				.set("count", dcResult.loserCount).get());

			this.text(q.get("abst")
				.set("count", dcResult.equal + dcResult.excluded).get());

			if (dcResult.tieInfo.length > 0) {
				this.text(q.get("dcTie").get());
			} else {
				this.text(q.get("explanation")
					.set("winner", this.candidateName(dcResult.winner))
					.set("loser", this.candidateName(dcResult.loser)).get());
			}

			let isLot = false;

			for (let i = 0; i < dcResult.tieInfo.length; i++) {
				const isLast = i === dcResult.tieInfo.length - 1;
				const ti = dcResult.tieInfo[i];

				if (ti.type === "points") {
					for (const id in ti.scores) {
						this.indentedText(q.get("dcTieRes")
							.set("candidateName", this.candidateName(id))
							.set("count", ti.scores[id])
							.set("score", ti.digit)
							.get());
					}

					if (isLast)
						this.text(q.get("dcTieBreak")
							.set("candidateName", this.candidateName(dcResult.winner))
							.set("score", ti.digit).get());

				} else {
					isLot = true;
				}
			}
		}

		this.text(q.get("result")
			.set("candidateName", this.candidateName(dcResult.winner))
			.set("spot", displaySpot)
			.set("listLong", listText)
			.get());
	}

	firstVoting() {
		const vg = this.group.sub("first").sub("voting");

		this.h2(vg.get("title").get());

		this.text(vg.get("ballot").get());

		const leadWinners = this.options.result.spots.slice(0, this.options.election.settings.leadElectionSeats);

		if (this.options.election.settings.leadElectionSeats > 0) {

			const names = this.joinWithUnd(leadWinners.map(v => this.candidateName(v)));

			this.text(vg.get("already")
				.set("candidateNames", names)
				.get());
		}

		for (const gender of this.genders()) {

			const candidates = this.options.election.candidates
				.filter(value => value.list === gender)
				.filter(value => !leadWinners.includes(value.id));

			const maxPoints = abschnittCElectionMaxPoints(candidates);

			this.text(vg.get("maxPoints", gender)
				.set("maxPoints", maxPoints).get());
		}

		this.text(vg.get("secret")
			.set("maleSecret", this.options.election.listVotes.secrets.male)
			.set("femaleSecret", this.options.election.listVotes.secrets.female)
			.get());

		const votes = Object.entries(this.options.election.listVotes.votes);

		this.text(vg.get("count")
			.set("count", votes.length)
			.set("maleCount", votes.filter(([_, v]) => v.list === "male").length)
			.set("femaleCount", votes.filter(([_, v]) => v.list === "female").length)
			.get());
	}

	firstApproval() {
		const vg = this.group.sub("first").sub("approval");

		this.h2(vg.get("title").get());

		this.text(vg.get("explanation").get());

		this.text(vg.get("results").get());

		const approvalMessages = this
			.getMessages<AbschnittCElectionResultMessageApproval>("approval")
			.map(value => {
				return {
					message: value,
					candidate: this.options.election.candidates.find(v => v.id === value.candidateId)!
				};
			});

		const sorted = approvalMessages.sort((a, b) => {
			return AbschnittCElectionResultSheetRenderer.sortCandidateByName(a.candidate, b.candidate);
		});

		this.table({
			headers: ["Titel", "Vorname", "Nachname", "", "Ja", "Nein"],
			rows: sorted.map(value => {
				const message = value.message;
				return [
					value.candidate.title ?? "",
					value.candidate.firstName,
					value.candidate.lastName,
					value.candidate.extra ?? "",
					message.votes[0].toString(),
					message.votes[1].toString()
				];
			}),
			widths: [1, 3, 3, 1, 1, 1]
		});

		this.text(vg.get("approved").get());

		const approved = approvalMessages.filter(value =>
			value.message.isApproved);

		this.list(approved.map(value => abschnittCCandidateName(value.candidate)));

		this.text(vg.get("rejected").get());

		const rejected = approvalMessages.filter(value => !value.message.isApproved);

		if (rejected.length > 0)
			this.list(rejected.map(value => abschnittCCandidateName(value.candidate)));
		else
			this.list([vg.get("none").get()]);

	}
}
